import { useState } from "react";
import { useGetIdentity, useNotify } from "ra-core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { MODULES, type ModuleId } from "@/modules/registry";
import type { ModuleChange, ModuleRow } from "@/modules/apps/ModulesManager";

/**
 * The company's organization_modules rows plus a writer for workspace
 * admins (RLS: own org, admins only).
 */
export const useOrgModuleRows = () => {
  const { identity, refetch: refetchIdentity } = useGetIdentity();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [busyModuleId, setBusyModuleId] = useState<ModuleId | null>(null);
  const orgId = (identity as { org_id?: number } | undefined)?.org_id;
  const isAdmin =
    (identity as { administrator?: boolean } | undefined)?.administrator ===
    true;

  const query = useQuery({
    queryKey: ["organization_modules", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organization_modules")
        .select("module_id, enabled, options");
      if (error) throw error;
      return (data ?? []) as ModuleRow[];
    },
    enabled: orgId != null,
  });

  /** Returns true when saved. */
  const apply = async (changes: ModuleChange[]) => {
    if (orgId == null || !changes.length) return false;
    setBusyModuleId(changes[changes.length - 1].moduleId);
    try {
      const { error } = await supabase.from("organization_modules").upsert(
        changes.map((change) => ({
          org_id: orgId,
          module_id: change.moduleId,
          enabled: change.enabled,
          ...(change.options ? { options: change.options } : {}),
        })),
        { onConflict: "org_id,module_id" },
      );
      if (error) throw error;
      await queryClient.invalidateQueries({
        queryKey: ["organization_modules"],
      });
      await refetchIdentity();
      const last = changes[changes.length - 1];
      if (!last.options) {
        notify(
          `${MODULES[last.moduleId].name} ${last.enabled ? "turned on" : "turned off"}`,
          { type: "success" },
        );
      }
      return true;
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Could not update the module",
        { type: "error" },
      );
      return false;
    } finally {
      setBusyModuleId(null);
    }
  };

  return {
    rows: query.data ?? [],
    isPending: query.isPending,
    isAdmin,
    busyModuleId,
    apply,
  };
};
